import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MagacinProduct, MagacinRemovalReason } from '../types';
import { magacinProductsAPI, magacinRemovalsAPI } from '../services/api';
import './Magacin.css';

const reasonOptions: Array<{ label: string; value: MagacinRemovalReason }> = [
  { label: 'Rashodovanje', value: 'rashodovanje' },
  { label: 'Poklon', value: 'poklon' },
  { label: 'Testiranje', value: 'testiranje' },
  { label: 'Oštećenje', value: 'ostecenje' },
  { label: 'Istek roka', value: 'istek_roka' },
  { label: 'Ostalo', value: 'ostalo' }
];

const toDate = (d: any) => (d instanceof Date ? d : d ? new Date(d) : new Date());

const MagacinRemovals: React.FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<MagacinProduct[]>([]);
  const [suggestions, setSuggestions] = useState<MagacinProduct[]>([]);

  const [selectedProduct, setSelectedProduct] = useState<MagacinProduct | null>(null);
  const [productQuery, setProductQuery] = useState('');
  const [currentStock, setCurrentStock] = useState<number>(0);
  const [unit, setUnit] = useState('kg');

  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState<MagacinRemovalReason>('rashodovanje');
  const [removalDate, setRemovalDate] = useState<Date>(new Date());
  const [responsiblePerson, setResponsiblePerson] = useState(user ? `${user.firstName} ${user.lastName}` : '');
  const [notes, setNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await magacinProductsAPI.getAll();
      setProducts(data);
      setSuggestions(data.slice(0, 10));
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri učitavanju proizvoda',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const complete = (e: AutoCompleteCompleteEvent) => {
    const q = (e.query || '').toLowerCase().trim();
    if (!q) {
      setSuggestions(products.slice(0, 10));
      return;
    }
    setSuggestions(products.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 10));
  };

  const onSelect = (p: MagacinProduct) => {
    setSelectedProduct(p);
    setProductQuery(p.name);
    setCurrentStock(Number(p.availableWeight || 0));
    setUnit(p.unit || 'kg');
    setQuantity(0);
  };

  const submit = async () => {
    if (!selectedProduct) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Izaberite proizvod', life: 3000 });
      return;
    }
    if (quantity <= 0) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Količina mora biti veća od 0', life: 3000 });
      return;
    }
    if (!responsiblePerson.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Odgovorna osoba je obavezna', life: 3000 });
      return;
    }

    setLoading(true);
    try {
      // Fetch fresh state before subtraction
      const fresh = await magacinProductsAPI.getById(selectedProduct._id);
      if (fresh.availableWeight < quantity) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Nedovoljno stanje',
          detail: `Dostupno je ${fresh.availableWeight} ${fresh.unit}`,
          life: 4000
        });
        setCurrentStock(fresh.availableWeight);
        setUnit(fresh.unit);
        return;
      }

      await magacinRemovalsAPI.create({
        productId: fresh._id,
        productName: fresh.name,
        productType: fresh.productType,
        quantity,
        unit: fresh.unit,
        removalDate: toDate(removalDate),
        reason,
        responsiblePerson: responsiblePerson.trim(),
        notes: notes || '',
        department: 'magacin'
      } as any);

      toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Stanje je umanjeno', life: 2500 });
      setQuantity(0);
      setNotes('');
      const refreshed = await magacinProductsAPI.getById(fresh._id);
      setCurrentStock(refreshed.availableWeight);
      setUnit(refreshed.unit);
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri skidanju sa stanja',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="magacin-page p-4">
      <Toast ref={toast} />

      <div className="magacin-header flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Skini proizvod sa stanja</h2>
          <div className="text-600 mt-1">Umanjenje stanja proizvoda iz magacina</div>
        </div>
        <div className="flex gap-2">
          <Button label="Nazad" icon="pi pi-arrow-left" severity="secondary" onClick={() => navigate('/magacin/products')} />
        </div>
      </div>

      <Card>
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-8">
            <label>Naziv proizvoda *</label>
            <AutoComplete
              value={productQuery}
              suggestions={suggestions}
              completeMethod={complete}
              field="name"
              dropdown
              placeholder="Unesite naziv proizvoda ili pretražite postojeće..."
              onChange={(e) => {
                const v: any = e.value;
                setProductQuery(typeof v === 'string' ? v : v?.name || '');
              }}
              onSelect={(e) => onSelect(e.value as MagacinProduct)}
            />
          </div>
          <div className="field col-12 md:col-4">
            <label>Trenutno stanje</label>
            <InputText value={`${currentStock} ${unit}`.trim()} disabled />
          </div>

          <div className="field col-12 md:col-6">
            <label>Količina za skidanje *</label>
            <InputNumber value={quantity} onValueChange={(e) => setQuantity(e.value ?? 0)} min={0} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Jedinica mere</label>
            <InputText value={unit} disabled />
          </div>

          <div className="field col-12 md:col-6">
            <label>Razlog skidanja *</label>
            <Dropdown value={reason} options={reasonOptions} onChange={(e) => setReason(e.value)} appendTo="self" />
          </div>
          <div className="field col-12 md:col-6">
            <label>Datum skidanja *</label>
            <Calendar value={removalDate} onChange={(e) => setRemovalDate(toDate(e.value))} dateFormat="dd.mm.yy" showIcon />
          </div>

          <div className="field col-12">
            <label>Odgovorna osoba *</label>
            <InputText value={responsiblePerson} onChange={(e) => setResponsiblePerson(e.target.value)} />
          </div>

          <div className="field col-12">
            <label>Napomene</label>
            <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} />
          </div>
        </div>

        <div className="flex justify-content-between align-items-center mt-3">
          <Button icon="pi pi-times" className="p-button-text" onClick={() => navigate('/magacin/products')} />
          <div className="flex gap-2">
            <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => navigate('/magacin/products')} />
            <Button label="Skini sa stanja" icon="pi pi-check" severity="warning" onClick={submit} loading={loading} />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MagacinRemovals;


